// Stale-tab recovery after a deploy (SC-3705).
//
// A tab opened before a deploy keeps the old index.js, which names the old
// hashed chunks. Amplify replaces the whole build, so the first lazy route the
// user opens afterwards 404s (`Failed to fetch dynamically imported module`).
// Vite dispatches `vite:preloadError` on window for that failure; a reload
// picks up the new index.html and the user lands on the view they asked for.
//
// One auto-reload per build: before reloading we record the build we are
// leaving in sessionStorage (which survives the reload). A failure is a stale
// chunk only if the running build differs from that record. After the reload
// the record names the build we left, so the new build gets its own one reload
// on a later deploy, however long the tab lives. If the new build is itself
// missing a chunk, its next failure records its own id; a second failure in
// that same build then finds its id already recorded and stops, so a broken
// deploy can't loop. That failure falls through to the error boundary and
// reaches Sentry as before.
//
// We don't preventDefault: the import still rejects, the boundary renders for
// the instant before the page unloads, and Sentry drops that event via
// `dropWhileReloadingForStaleChunk` (see sentry.ts). preventDefault would instead
// resolve the lazy() import to undefined and raise a second, misleading error.

export const STALE_CHUNK_RELOAD_KEY = 'ekom:stale-chunk-reloaded-from';

// A reload that hasn't unloaded the page by now never will; stop muting Sentry
// rather than leave the tab silent for the rest of its life.
export const RELOAD_GRACE_MS = 10_000;

let reloading = false;
let graceTimer: ReturnType<typeof setTimeout> | undefined;

export function isReloadingForStaleChunk(): boolean {
  return reloading;
}

/** Sentry `beforeSend`: drop events raised while the stale-chunk reload is in flight. */
export function dropWhileReloadingForStaleChunk<T>(event: T): T | null {
  return reloading ? null : event;
}

function claimReload(buildId: string): boolean {
  try {
    if (sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) === buildId) return false;
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, buildId);
    return true;
  } catch {
    // Storage blocked: without the record a missing chunk could reload forever.
    return false;
  }
}

/** Returns an uninstall function (tests). */
export function installStaleChunkReload({
  buildId = __BUILD_ID__,
  reload = () => window.location.reload(),
}: { buildId?: string; reload?: () => void } = {}): () => void {
  const onPreloadError = () => {
    if (reloading || !claimReload(buildId)) return;
    reloading = true;
    graceTimer = setTimeout(() => {
      reloading = false;
    }, RELOAD_GRACE_MS);
    reload();
  };
  window.addEventListener('vite:preloadError', onPreloadError);
  return () => {
    window.removeEventListener('vite:preloadError', onPreloadError);
    clearTimeout(graceTimer);
    reloading = false;
  };
}
